import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CustomDateAdapter } from '../../../../../shared/services/custom-date-adapter';

export interface ProximaAcaoData {
  titulo: string;
  descricao: string | null;
  /** dd/MM/yyyy. */
  data: string | null;
}

export interface ProximaAcaoResult {
  descricao: string;
  /** dd/MM/yyyy ou null quando o usuário não define data. */
  data: string | null;
}

/**
 * Preenchimento rápido da próxima ação a partir do card (CA07) — sem abrir a ficha.
 * A Próxima Ação é vinculada à entidade Tarefas; o detalhamento vem na Story de Tarefas.
 */
@Component({
  selector: 'app-proxima-acao-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
  template: `
    <h2 mat-dialog-title class="dialog-title">Definir próxima ação</h2>

    <mat-dialog-content class="dialog-content">
      <p class="dialog-text">{{ data.titulo }}</p>

      <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
        <mat-label>Descrição da ação</mat-label>
        <input matInput [(ngModel)]="descricao" maxlength="256"
          placeholder="Ex.: Ligar para confirmar reunião" />
      </mat-form-field>

      <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
        <mat-label>Data</mat-label>
        <input matInput [matDatepicker]="picker" [(ngModel)]="dataSelecionada" />
        <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-datepicker #picker></mat-datepicker>
      </mat-form-field>
    </mat-dialog-content>

    <mat-dialog-actions align="end" class="dialog-actions">
      <button mat-stroked-button type="button" class="btn-secondary" (click)="cancelar()">Cancelar</button>
      <button mat-flat-button type="button" class="btn-primary" [disabled]="!valida" (click)="confirmar()">
        <mat-icon class="material-symbols-outlined">check</mat-icon>
        Salvar
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .dialog-title { margin: 0; padding: 20px 24px 12px; }
    .dialog-content {
      width: 400px; max-width: 92vw; padding: 8px 24px 12px;
      display: flex; flex-direction: column; gap: 16px;
    }
    .dialog-text { margin: 0; font-size: 13px; color: #5f6368; line-height: 1.4; }
    .dialog-actions { gap: 8px; margin: 0; padding: 16px 24px 20px; border-top: 1px solid #e5e7eb; }
    :host-context(body.dark-mode) .dialog-text { color: #cac4d0; }
    :host-context(body.dark-mode) .dialog-actions { border-color: #49454f; }
  `],
})
export class ProximaAcaoDialogComponent {
  descricao: string;
  dataSelecionada: Date | null;

  constructor(
    public dialogRef: MatDialogRef<ProximaAcaoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ProximaAcaoData
  ) {
    this.descricao = data.descricao ?? '';
    this.dataSelecionada = this.paraDate(data.data);
  }

  get valida(): boolean {
    return this.descricao.trim().length > 0;
  }

  confirmar(): void {
    if (!this.valida) return;
    this.dialogRef.close({
      descricao: this.descricao.trim(),
      data: this.paraTextoBR(this.dataSelecionada),
    } as ProximaAcaoResult);
  }

  cancelar(): void {
    this.dialogRef.close(null);
  }

  private paraDate(texto: string | null): Date | null {
    if (!texto) return null;
    const [d, m, a] = texto.split('/').map(n => parseInt(n, 10));
    if (!d || !m || !a) return null;
    return new Date(a, m - 1, d);
  }

  private paraTextoBR(data: Date | null): string | null {
    if (!data) return null;
    return data.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }
}
